import { Controller } from "@hotwired/stimulus"

// facilities--filter-cascade — narrows a chain of FILTER dropdowns
// (Site → Building → Zone → AssetGroup) as soon as a level changes, without
// waiting for the form to be submitted. The server narrows the same lists on
// the next GET; this is what makes the bar agree with itself in between.
//
// WHY NOT ui--facilities--location-cascade. The engine ships a cascade
// controller with the same shape, and this deliberately does not reuse it:
// that one implements FORM semantics, where the chain leads to exactly one
// terminal value. There, an unpicked parent means "hide every child option"
// and "disable the child", which guides the user top-down and stops them
// submitting a child that contradicts its parent.
//
// A filter bar owes the opposite. "All zones" is a legitimate, common
// position, and filtering straight by zone without first naming its site has
// to keep working — that is how these lists are used. So here an unpicked
// parent means "show everything", and no level is ever disabled. Those two
// rules are the entire difference, and they are not a setting on the engine
// controller; reusing it would trade a live-narrowing bar for one that locks
// three of its four selects until you work down from the top.
//
// Contract per level (same wrapper shape as the engine's, so the two stay
// recognisable side by side):
//   data-facilities--filter-cascade-target="level"
//   data-full-options='[{value, text, parent_id?}, ...]'   ← source of truth
//
// The payload is the source of truth rather than the live <option> tree
// because Tom Select rebuilds that tree from its own internal dict and
// ignores post-init edits to it.
export default class extends Controller {
  static targets = ["level"]

  connect() {
    // `null`, not `[]`, when a payload will not parse. Those two read the same
    // but mean opposite things to the filter below: `[]` is "this level has no
    // options", which would wipe a select clean the first time its parent
    // changed — turning a malformed attribute into apparent data loss. `null`
    // is "no source of truth here", and the filter leaves such a level alone.
    // Unreachable today (the ERB emits to_json), which is exactly why the
    // failure mode should not be the destructive one.
    this.fullOptions = this.levelTargets.map((el) => {
      try {
        return JSON.parse(el.dataset.fullOptions || "[]")
      } catch (_) {
        return null
      }
    })

    // Listeners go on the underlying <select>, not on the wrapper via
    // data-action: Tom Select dispatches change on the <select> and it
    // bubbles, so a wrapper action would run the cascade twice per click.
    this.changeHandlers = new Map()
    this.levelTargets.forEach((wrapper, idx) => {
      const select = wrapper.querySelector("select")
      if (!select) return
      const handler = () => this.filterFrom(idx)
      select.addEventListener("change", handler)
      this.changeHandlers.set(select, handler)
    })
  }

  disconnect() {
    this.changeHandlers?.forEach((handler, select) => {
      select.removeEventListener("change", handler)
    })
    this.changeHandlers = null
  }

  // Re-narrows every level below `startIdx`, top-down.
  //
  // A child is matched against the set of parents that are STILL REACHABLE,
  // not against the parent's selected value alone. That distinction is what
  // makes narrowing transitive: picking a building leaves the zone select
  // with no value (its previous one rarely survives), so a parent-value-only
  // rule would hand the group level "no parent picked → show everything" and
  // leave every aisle in the company listed under one building. Walking
  // top-down means each level is already narrowed when the next one reads it.
  filterFrom(startIdx) {
    for (
      let idx = Math.max(startIdx, 0);
      idx < this.levelTargets.length - 1;
      idx++
    ) {
      const parentTs = this.tomSelectFor(this.levelTargets[idx])
      const childTs = this.tomSelectFor(this.levelTargets[idx + 1])
      if (!childTs) continue

      const parentValue = parentTs ? String(parentTs.getValue() ?? "") : ""
      const reachable = this.reachableParentIds(idx, parentTs, parentValue)
      const currentValue = String(childTs.getValue() ?? "")
      const options = this.fullOptions[idx + 1]
      // No parsable source for this level — leave whatever the server rendered
      // in place rather than rebuilding it from nothing.
      if (options === null || options === undefined) continue

      const filtered = options.filter((option) => {
        // The sentinel ("All zones" and friends) is how the operator widens
        // back out, so it survives every narrowing. It has to be present in
        // the payload for that to work — rebuilding the option list from a
        // payload that omits it silently strips it on the first change.
        if (String(option.value) === "") return true
        if (reachable === null) return true
        // A building mid-backfill has no facilities_site_id (see the pack's
        // Fork of concerns note). Under a specific site it is hidden, matching
        // what the server's own site filter does — otherwise the list would
        // offer a building the next GET then drops.
        if (option.parent_id == null || option.parent_id === "") return false
        return reachable.has(String(option.parent_id))
      })

      const survives = filtered.some((o) => String(o.value) === currentValue)

      // Order matters: clearOptions() does NOT remove the currently-selected
      // option, so the selection is cleared first (silently, to avoid firing a
      // change that would re-enter this method), then the dict is wiped and
      // repopulated. Otherwise a stale parent's child outlives a narrower
      // filter.
      childTs.clear(true)
      childTs.clearOptions()
      filtered.forEach((o) =>
        childTs.addOption({ value: String(o.value), text: o.text }),
      )
      childTs.refreshOptions(false)

      if (currentValue && survives) childTs.setValue(currentValue, true)
    }
  }

  // The parent ids a child may still hang from:
  //   - an explicit selection → exactly that one
  //   - nothing selected, but the parent is itself narrowed → those options,
  //     so a constraint from further up keeps travelling down
  //   - nothing selected and the parent still shows its whole payload → null,
  //     meaning "no constraint", which is what keeps "All sites" showing
  //     every zone rather than none
  reachableParentIds(parentIdx, parentTs, parentValue) {
    if (parentValue !== "") return new Set([parentValue])
    if (!parentTs) return null

    const parentPayload = this.fullOptions[parentIdx]
    // Same reasoning: without a payload there is nothing to compare the
    // parent's visible set against, so it constrains nothing.
    if (parentPayload === null || parentPayload === undefined) return null

    const visible = Object.keys(parentTs.options || {}).filter((v) => v !== "")
    const payloadSize = parentPayload.filter(
      (o) => String(o.value) !== "",
    ).length

    return visible.length < payloadSize ? new Set(visible) : null
  }

  tomSelectFor(wrapper) {
    const select = wrapper?.querySelector("select")
    return select?.tomselect ?? null
  }
}
